/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 异常堆栈相关处理函数
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */


import {StackInfo, StackFrame, StackInfoFrame} from './interface/options.interface';
import {joinRegExp} from './utils/misc';

/**
 * 检测本帧是否是当前文件帧，例如 CDN 文件地址
 */
function isSelfFrame(frame: StackFrame) {
    const filename = frame.filename || '';
    // 如果是从 CDN 获取
    const isFromCDN = filename.indexOf('weirwood-sdk') !== -1;

    // 如果是敏感函数
    const isSensitiveFunction = /captureException$/.test(filename);
    return isFromCDN || isSensitiveFunction;
}

/**
 * 规范化异常堆栈列表
 * @param stackInfo Tracekit 计算出来的错误信息
 * @param options 选项
 * @param limit 限制堆栈帧数量
 */
export function prepareFrames(
    stackInfo: StackInfo,
    options: {trimHeadFrames?: number, includePaths?: any[]},
    limit?: number
): StackFrame[] {
    let frames: StackFrame[] = [];
    if (stackInfo.stack && stackInfo.stack.length) {
        for (const stack of stackInfo.stack) {
            const frame = normalizeFrame(stack, stackInfo.url || '', options.includePaths);
            frames.push(frame);
        }

        // e.g. frames captured via captureMessage throw
        // 如果使用 captureMessage 手动触发，需要设置头部帧不是由 app 内部代码触发
        if (options && options.trimHeadFrames) {
            for (let j = 0; j < options.trimHeadFrames && j < frames.length; j++) {
                frames[j].in_app = false;
            }
        }
    }
    frames = frames.filter(f => !isSelfFrame(f));
    frames = frames.slice(0, limit);
    return frames;
}

/**
 * 格式化异常堆栈帧，将 Tracekit 收集到的帧格式转化为 StackFrame，也即 StackInfoFrame -> StackFrame
 * @param frame
 * @param stackInfoUrl
 */
export function normalizeFrame(frame: StackInfoFrame, stackInfoUrl: string, includePaths?: any[]): StackFrame {
    // normalize the frames data
    const normalized: any = {
        // Case when we don't have any information about the error
        // E.g. throwing a string or raw object, instead of an `Error` in Firefox
        // Generating synthetic error doesn't add any value here
        //
        // We should probably somehow let a user know that they should fix their code
        filename: frame.url || stackInfoUrl,
        lineno: frame.line,
        colno: frame.column,
        function: frame.func || '?'
    };

    // 处理 in_app 逻辑
    if (includePaths && includePaths.length > 0) {
        const regex = joinRegExp(includePaths);
        const filenameMatch = regex.test(normalized.filename);
        const functionMatch = /(Weirwood|Tracekit)/.test(normalized['function']);
        const minifileMatch = /weirwood\-sdk/.test(normalized.filename);
        normalized.in_app = filenameMatch;

        if (functionMatch || minifileMatch) {
            normalized.in_app = false;
        }
    }
    else {
        normalized.in_app = false;
    }

    return normalized;
}

/**
 * 处理堆栈，并返回
 * 1. 如果有堆栈，将堆栈倒序
 * 2. 如果没有堆栈，将 fileurl 和 lineno 拼接成一个堆栈帧
 * @param frames 格式化好的堆栈
 * @param fileurl 错误来源
 * @param lineno 错误行
 * @returns Array<StackFrame>
 */
export function computeFrames(frames: StackFrame[], fileurl: string, lineno: number): Array<StackFrame> {
    const hasFrames = frames && frames.length;
    if (hasFrames) {
        // Sentry expects frames oldest to newest
        // and JS sends them as newest to oldest
        return frames.reverse();
    }
    else if (fileurl) {
        return [{
            filename: fileurl,
            lineno: lineno || 0,
            in_app: true
        }];
    }
    return [];
}

/**
 * 获取错误来源，如果有堆栈，返回第一帧的 filename，否则返回传入的 fileurl
 * @param frames 格式化好的堆栈
 * @param fileurl 错误来源
 * @returns string
 */
export function computeTransactionUrl(frames: StackFrame[], fileurl: string): string {
    const hasFrames = frames && frames.length;
    if (hasFrames) {
        return frames[0].filename || fileurl;
    }
    return fileurl;
}
